import classNames, { Argument } from "classnames/dedupe";
import React, { ElementType, useMemo } from "react";

export type TTextElement =
  | "span"
  | "p"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | ElementType;

export type TTextVariant =
  | "text-small"
  | "text-small-bold"
  | "text-regular"
  | "text-paragraph"
  | "text-medium"
  | "text-bold"
  | "text-uppercase"
  | "text-lowercase"
  | "text-capitalize"
  | "text-bold-header"
  | "text-small-header"
  | "text-nav"
  | "text-desc"
  | "text-menu"
  | "text-menu-medium"
  | "mobile-header-20-bold"
  | "mobile-header-12-bold"
  | "desktop-header-l"
  | "section-title"
  | "h1-enriched"
  | "h2-enriched"
  | "h3-enriched"
  | "h4-enriched"
  | "h-sticky"
  | "h-page"
  | "h-menu"
  | "h-card"
  | "h2-widget"
  | "h3-menu"
  | "h3-pdp"
  | "h3-loyalty"
  | "p-big"
  | "p-medium"
  | "p-banner"
  | "h-menu-medium"
  | "h-menu-small-bold"
  | "h-menu-small-medium"
  | "highlighter highlighter--active"
  | "highlighter highlighter--inactive"
  | "highlighter highlighter--passed"
  | "d-header-14-bold";

export const TypographyVariants: Record<TTextVariant, TTextVariant> = {
  "text-small": "text-small",
  "text-small-bold": "text-small-bold",
  "text-regular": "text-regular",
  "text-paragraph": "text-paragraph",
  "text-medium": "text-medium",
  "text-bold": "text-bold",
  "text-uppercase": "text-uppercase",
  "text-lowercase": "text-lowercase",
  "text-capitalize": "text-capitalize",
  "text-bold-header": "text-bold-header",
  "text-small-header": "text-small-header",
  "text-nav": "text-nav",
  "text-desc": "text-desc",
  "text-menu": "text-menu",
  "text-menu-medium": "text-menu-medium",
  "mobile-header-20-bold": "mobile-header-20-bold",
  "mobile-header-12-bold": "mobile-header-12-bold",
  "desktop-header-l": "desktop-header-l",
  "section-title": "section-title",
  "h1-enriched": "h1-enriched",
  "h2-enriched": "h2-enriched",
  "h3-enriched": "h3-enriched",
  "h4-enriched": "h4-enriched",
  "h-sticky": "h-sticky",
  "h-page": "h-page",
  "h-menu": "h-menu",
  "h-card": "h-card",
  "h2-widget": "h2-widget",
  "h3-menu": "h3-menu",
  "h3-pdp": "h3-pdp",
  "h3-loyalty": "h3-loyalty",
  "p-big": "p-big",
  "p-medium": "p-medium",
  "p-banner": "p-banner",
  "h-menu-medium": "h-menu-medium",
  "h-menu-small-bold": "h-menu-small-bold",
  "h-menu-small-medium": "h-menu-small-medium",
  "highlighter highlighter--active": "highlighter highlighter--active",
  "highlighter highlighter--inactive": "highlighter highlighter--inactive",
  "highlighter highlighter--passed": "highlighter highlighter--passed",
  "d-header-14-bold": "d-header-14-bold",
} as const;

export type TTextDecoration = "none" | "underline" | "line-through";

export type TTextColor = "white" | "blue" | "darkBlue" | "red";

const textDecorationMapping: Record<TTextDecoration, string> = {
  none: "text-decoration-none",
  underline: "text-decoration-underline",
  "line-through": "text-decoration-line-through",
} as const;

const colorMapping = {
  white: "text-color-white",
  blue: "text-color-blue",
  darkBlue: "text-color-dark-blue",
  red: "text-color-red",
};

export interface ITypographyProps {
  /**
   * Sets a custom html element for this component
   *
   * @default 'span'
   *
   * @type {"span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"}
   */
  as?: TTextElement;

  /**
   * Visual representation of component, from design tokens
   */
  variant?: TTextVariant;

  /**
   * Color representation of component, from design tokens
   *
   * @type {"white" | "blue" | "darkBlue" | "red"}
   *
   * @default 'blue'
   */
  color?: TTextColor;

  /**
   * Text decoration
   *
   * @type "underline" | "line-through";
   */
  decoration?: TTextDecoration;

  /**
   * The custom set of css classes used to style component
   */
  classes?: {
    /** Styles applied to outer element. */
    root?: Argument;
  };
  title?: any;
  dangerouslySetInnerHTML?: { __html: string };
  hasTruncated?: boolean;
  lineClamp?: number;
  [key: string]: any;
}

export const Typography = React.forwardRef<ElementType, ITypographyProps>(
  (
    {
      as: Component = "span",
      variant,
      color,
      hasTruncated = false,
      lineClamp = 2,
      decoration = null,
      classes,
      children,
      ...other
    },
    ref,
  ) => {
    const maxLinesClass =
      hasTruncated && lineClamp ? `max-lines--${lineClamp}` : ``;
    const rootClasses = classNames(
      variant,
      decoration ? textDecorationMapping[decoration] : undefined,
      colorMapping[color],
      {
        "max-lines word-break-word": hasTruncated,
        [maxLinesClass]: lineClamp,
      },
      classes?.root,
    );

    return (
      <Component ref={ref} className={rootClasses} {...other}>
        {children}
      </Component>
    );
  },
);
